import { EntityIcon } from '@common/Entity/EntityIcons';
import { FabricSVGIcons } from '@common/Fabric/icons';
import { FabricProviderType } from '@common/Fabric/types';
import { SPARK_PROVIDER } from '@common/Fabric/types/spark';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { titleCase } from '@common/utils/metadataUtils';
import { renderTimeColumnWithEllipsis } from '@common/utils/renderUtils';
// import { Icon } from '@prophecy/icons';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { BaseColor, theme } from '@prophecy/theme';
import {
  Avatar,
  Badges,
  Button,
  Card,
  Ellipsis,
  LabelWithHint,
  overlayStyle,
  ProgressBar,
  Separator,
  Stack,
  StackItem,
  Tags,
  Text,
  Tooltip
} from '@prophecy/ui-v3';
import { camelCase } from 'lodash-es';
import React from 'react';
import styled from 'styled-components';

import { InfoCardType, StatusMapKeys } from '../../../../data/apis/transpileTypes';
import { LanguageIconMap } from '../../../Metadata/EntityDetail/ProjectDetail/Components/styled';
import { FileFormatLabels, FileFormats, StatusMap, StatusTone } from '../../config';
import { NotAssigned } from '../util';

export const StyledInfoCard = styled(Card)`
  &&,
  &&:hover {
    box-shadow: none;
    padding-right: ${theme.spaces.x6};
    border: 1px solid ${theme.colors.gray200};
    display: flex;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 1;
  }
`;

const StyledStackItem = styled(StackItem)`
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: ${theme.spaces.x6};
`;

export const StyledContent = styled.span`
  max-width: 110px;
  display: inline-flex;
`;

const StyledAvatar = styled(Avatar)`
  background: ${theme.colors.gray100};
  color: ${theme.colors.gray600};
`;

export const StyledSeparator = styled(Separator)`
  margin: ${theme.spaces.x10} 0;
`;

const StyledTags = styled(Tags)`
  width: fit-content;
  max-width: 70%;
  cursor: default;
  padding: ${theme.spaces.x12};
`;

export const RightInfoPanel = ({
  infoData,
  overallSummaryUI
}: {
  infoData?: InfoCardType;
  overallSummaryUI: React.ReactNode;
}) => {
  const projectId = infoData?.projectId;
  const fabricId = infoData?.fabricId;
  const projectName = infoData?.projectName;
  const provider = infoData?.isProphecyManagedFabric ? SPARK_PROVIDER.PROPHECY : infoData?.fabricType;

  const FabricProviderIcon = provider ? FabricSVGIcons[FabricProviderType.Spark][provider] : undefined;
  const getEntityNameWithIcon = (name = '--', isProject?: boolean) => (
    <Stack direction='horizontal' gap={theme.spaces.x4} alignY='center'>
      {isProject ? (
        <EntityIcon entity={Entity.Project} size='xs' />
      ) : (
        provider && FabricProviderIcon && <FabricProviderIcon name={provider} size='xs' />
      )}
      <Text level='sm' tone={theme.colors.brand600}>
        <StyledContent>
          <Ellipsis tooltip={true}>{name}</Ellipsis>
        </StyledContent>
      </Text>
    </Stack>
  );

  const source = infoData?.source as FileFormats;
  const status = infoData?.status.toLowerCase() as StatusMapKeys;
  const LangIcon = LanguageIconMap[infoData?.language || BackEndCodeLanguage.scala];

  return (
    <StyledInfoCard hasHoverEffect={false}>
      <Stack gap={theme.spaces.x24} align='space-between' height='100%' width='100%'>
        <StyledStackItem grow='1'>
          <Stack gap={theme.spaces.x24} height='100%' width='100%'>
            <Stack width='100%' gap={theme.spaces.x16}>
              <LabelWithHint hint='It is calculated by adding the coverage % of all the graphs and dividing by the total no. of graphs.'>
                <Text tone={theme.colors.gray900} weight={theme.fontWeight.medium} level='sm'>
                  Overall Coverage
                </Text>
              </LabelWithHint>
              <Tooltip
                placement='bottom'
                overlayStyle={{ ...overlayStyle, background: theme.colors.white, boxShadow: theme.shadows.lg }}
                title={overallSummaryUI}
                forceTooltipContentColor={false}>
                <Stack width='100%' direction='horizontal' alignY='center' gap={theme.spaces.x10}>
                  <ProgressBar variant={'loading'} value={infoData?.transpilePercent || 0} height='8px' width='100%' />{' '}
                  {infoData?.transpilePercent || 0}%
                </Stack>
              </Tooltip>
            </Stack>
            <StyledSeparator />
            <Stack width='100%' direction='horizontal' align='space-between' alignY='center'>
              <Text tone={theme.colors.gray900} weight={theme.fontWeight.medium} level='sm'>
                Status
              </Text>
              {status ? (
                <Badges tone={StatusTone[status] as BaseColor} icon={StatusMap[status]} iconPlacement='left'>
                  {titleCase(camelCase(status))}
                </Badges>
              ) : (
                <i>No content</i>
              )}
            </Stack>
            <StyledSeparator orientation='horizontal' />
            <Stack width='100%' direction='horizontal' align='space-between' alignY='center' gap={theme.spaces.x8}>
              <Text tone={theme.colors.gray900} weight={theme.fontWeight.medium} level='sm'>
                Source
              </Text>
              <StyledTags tone={BaseColor.gray}>
                <Ellipsis tooltip={true}>{FileFormatLabels[source]}</Ellipsis>
              </StyledTags>
            </Stack>
            <StyledSeparator orientation='horizontal' />
            <Stack width='100%' direction='horizontal' align='space-between' alignY='center'>
              <Text tone={theme.colors.gray900} weight={theme.fontWeight.medium} level='sm'>
                Language
              </Text>
              <StyledTags tone='gray' icon={<LangIcon />} size='l'>
                {titleCase(infoData?.language || '')}
              </StyledTags>
            </Stack>
            <StyledSeparator orientation='horizontal' />
            <Stack width='100%' direction='horizontal' align='space-between' alignY='center'>
              <Text tone={theme.colors.gray900} weight={theme.fontWeight.medium} level='sm'>
                Project
              </Text>
              {projectId && projectName !== NotAssigned ? (
                <Button
                  variant='link'
                  elementType='link'
                  to={Private_Routes.Project.home.getUrl({ uid: projectId })}
                  data-test-id='right-info-panel-project-link-button'>
                  {getEntityNameWithIcon(projectName, true)}
                </Button>
              ) : (
                getEntityNameWithIcon(projectName, true)
              )}
            </Stack>
            <StyledSeparator orientation='horizontal' />
            <Stack width='100%' direction='horizontal' align='space-between' alignY='center'>
              <Text tone={theme.colors.gray900} weight={theme.fontWeight.medium} level='sm'>
                Fabric
              </Text>
              {fabricId ? (
                <Button
                  variant='link'
                  elementType='link'
                  to={Private_Routes.Fabric.home.getUrl({ uid: fabricId })}
                  data-test-id='right-info-panel-fabric-link-button'>
                  {getEntityNameWithIcon(infoData?.fabricName)}
                </Button>
              ) : (
                getEntityNameWithIcon(infoData?.fabricName)
              )}
            </Stack>
          </Stack>
        </StyledStackItem>
        <StyledSeparator orientation='horizontal' />
        <Stack gap={theme.spaces.x10}>
          <Stack width='100%' gap={theme.spaces.x12} align='space-between' direction='horizontal' alignY='center'>
            <Text tone={theme.colors.gray600} weight={theme.fontWeight.medium} level='xs'>
              Modified:
            </Text>
            <Text tone={theme.colors.gray500} level='xs'>
              <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center' width='100%'>
                <StyledContent>
                  {infoData?.updatedAt ? renderTimeColumnWithEllipsis(infoData?.updatedAt) : '--'}
                </StyledContent>
                {infoData?.updatedBy && (
                  <Tooltip title={infoData?.updatedBy}>
                    <div>
                      <StyledAvatar size='xs' text={infoData.updatedBy} />
                    </div>
                  </Tooltip>
                )}
              </Stack>
            </Text>
          </Stack>
          <Stack width='100%' gap={theme.spaces.x12} align='space-between' direction='horizontal' alignY='center'>
            <Text tone={theme.colors.gray600} weight={theme.fontWeight.medium} level='xs'>
              Created:
            </Text>
            <Text tone={theme.colors.gray500} level='xs'>
              <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center' width='100%'>
                <StyledContent>
                  {infoData?.importedAt ? renderTimeColumnWithEllipsis(infoData?.importedAt) : '--'}
                </StyledContent>
                {infoData?.createdBy && (
                  <Tooltip title={infoData?.createdBy}>
                    <div>
                      <StyledAvatar size='xs' text={infoData.createdBy} />
                    </div>
                  </Tooltip>
                )}
              </Stack>
            </Text>
          </Stack>
          <Stack width='100%' direction='horizontal' align='space-between' gap={theme.spaces.x12} alignY='center'>
            <Text tone={theme.colors.gray600} weight={theme.fontWeight.medium} level='xs'>
              Fork Mode:
            </Text>
            <Text tone={theme.colors.gray500} level='xs'>
              {infoData?.forkMode || '--'}
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </StyledInfoCard>
  );
};
